// Rug Room Shots: the "Outdoor & sunroom list" panel of the Rooms tab (build spec 4.4, 5.5 and 9.6).
// Rows are WebID prefixes (settings rows scene:<PREFIX>): Outdoor only, Sunroom only, Outdoor + sunroom (two
// shots) or "Not on the list" (an exception that carves a longer code out of a shorter one). The longest
// matching code wins. Sunroom comes ONLY from this list; nobody can pick it by hand. Changes apply to plans made
// afterwards. GET /scenes, PUT /scenes/{prefix} (upsert), DELETE /scenes/{prefix} (soft delete); every change is
// logged with the user's email.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Alert, CircularProgress, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Chip,
  Collapse,
} from '@mui/material';
import { MdAdd, MdEdit, MdDelete, MdRefresh, MdExpandMore, MdExpandLess } from 'react-icons/md';
import rrsApi from './api';
import {
  primaryButtonSx, fmtInt, NyTime, ConfirmDialog, serverText, styleColor,
} from './common';

const PREFIX_RE = /^[A-Z0-9][A-Z0-9-]{0,31}$/;
export const SCENE_MODES = [
  { value: 'outdoor', label: 'Outdoor only', short: 'Outdoor', color: '#2E7D32' },
  { value: 'sun', label: 'Sunroom only', short: 'Sunroom', color: styleColor('SU') },
  { value: 'both', label: 'Outdoor + sunroom (two shots)', short: 'Outdoor + sunroom', color: '#00838F' },
  { value: 'none', label: 'Not on the list (exception)', short: 'Not on the list', color: '#757575' },
];
const modeInfo = (m) => SCENE_MODES.find((x) => x.value === m) || { value: m, label: m, short: m, color: '#757575' };

const HELP = "Codes are the start of a WebID (CBN4 matches CBN4xx). The longest matching code wins, so a 'Not on the list' "
  + 'row for CYR carves Cyrus Shag out of CY. Outdoor only = an outdoor room; Sunroom only = a sunroom; Outdoor + sunroom = '
  + 'one shot in each. Rugs not on the list keep the usual rule (outdoor rugs get outdoor rooms). Changes apply to plans '
  + 'made afterwards.';

const EMPTY_FORM = { prefix: '', mode: 'both', label: '', note: '', existing: false };

const SceneListPanel = ({ notify, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [removeRow, setRemoveRow] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rrsApi.listScenes();
      setData(res || { items: [] });
      setError('');
    } catch (e) {
      setError(e && e.status === 404 ? 'The server has not been updated for the Outdoor & sunroom list yet.' : serverText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Loaded once on mount (about 80 small rows), so the collapsed header can show the counts.
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => (data && Array.isArray(data.items) ? data.items : []), [data]);
  const shown = useMemo(() => {
    const s = q.trim().toUpperCase();
    if (!s) return items;
    return items.filter((r) => String(r.prefix).includes(s) || String(r.label || '').toUpperCase().includes(s)
      || String(r.note || '').toUpperCase().includes(s));
  }, [items, q]);
  const counts = useMemo(() => {
    const c = {};
    items.forEach((r) => { c[r.mode] = (c[r.mode] || 0) + 1; });
    return c;
  }, [items]);

  const cleanPrefix = (form && form.prefix ? form.prefix : '').trim().toUpperCase();
  const prefixOk = PREFIX_RE.test(cleanPrefix);
  const existingRow = form && !form.existing ? items.find((r) => r.prefix === cleanPrefix) : null;
  const labelOk = !form || form.label.length <= 60;
  const noteOk = !form || form.note.length <= 300;

  const openAdd = () => { setForm({ ...EMPTY_FORM }); setFormError(''); };
  const openEdit = (r) => {
    setForm({ prefix: r.prefix, mode: r.mode || 'both', label: r.label || '', note: r.note || '', existing: true });
    setFormError('');
  };

  const save = async () => {
    if (!form || !prefixOk || !labelOk || !noteOk) return;
    setFormBusy(true);
    setFormError('');
    try {
      await rrsApi.saveScene(cleanPrefix, { mode: form.mode, label: form.label.trim(), note: form.note.trim() });
      notify(`${cleanPrefix}: ${modeInfo(form.mode).label}. Plans made from now on use it.`, 'success');
      setForm(null);
      await load();
    } catch (e) {
      setFormError(serverText(e));
    } finally {
      setFormBusy(false);
    }
  };

  const doRemove = async () => {
    if (!removeRow) return;
    setRemoveBusy(true);
    try {
      await rrsApi.removeScene(removeRow.prefix);
      notify(`${removeRow.prefix} removed from the Outdoor & sunroom list.`, 'success');
      setRemoveRow(null);
      await load();
    } catch (e) {
      notify(serverText(e), 'error');
    } finally {
      setRemoveBusy(false);
    }
  };

  const workerReady = !data || data.workerReady !== false;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          onClick={() => setOpen((v) => !v)} endIcon={open ? <MdExpandLess /> : <MdExpandMore />}
          sx={{ fontWeight: 700, textTransform: 'none', color: 'text.primary', px: 0.5 }}
        >
          Outdoor &amp; sunroom list
        </Button>
        {data && (
          <Typography variant="caption" color="text.secondary">
            {fmtInt(items.length)} code{items.length === 1 ? '' : 's'}
            {SCENE_MODES.filter((m) => counts[m.value]).map((m) => ` · ${m.short} ${fmtInt(counts[m.value])}`).join('')}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {open && (
          <>
            {loading && <CircularProgress size={18} />}
            <Button size="small" startIcon={<MdRefresh />} onClick={load} disabled={loading}>Refresh</Button>
            <Button size="small" variant="outlined" startIcon={<MdAdd />} onClick={openAdd}>Add</Button>
          </>
        )}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{HELP}</Typography>
        {!workerReady && <Alert severity="info" sx={{ mt: 1 }}>Takes effect when the worker is updated.</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        {data && (
          <>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5, mb: 1 }}>
              <TextField size="small" label="Find a code, label or note" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} />
              {data.countsAt && (
                <Typography variant="caption" color="text.secondary">
                  Active rugs counted <NyTime ts={data.countsAt} variant="caption" /> (New York)
                </Typography>
              )}
            </Stack>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Prefix</TableCell>
                    <TableCell>Gets</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell align="right">Active rugs</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                        {items.length ? 'No code matches.' : 'The list is empty. Add a code to give rugs outdoor or sunroom shots.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {shown.map((r) => {
                    const m = modeInfo(r.mode);
                    return (
                      <TableRow key={r.prefix} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.prefix}</TableCell>
                        <TableCell>
                          <Chip
                            size="small" label={m.short}
                            sx={{ bgcolor: `${m.color}1F`, color: m.color, border: `1px solid ${m.color}55`, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" noWrap title={r.label || ''}>{r.label || ''}</Typography></TableCell>
                        <TableCell sx={{ maxWidth: 260 }}><Typography variant="body2" color="text.secondary" noWrap title={r.note || ''}>{r.note || ''}</Typography></TableCell>
                        <TableCell align="right">
                          {r.matchedWebIds === null || r.matchedWebIds === undefined ? (
                            <Tooltip title="Counted by the worker after its next update."><span>-</span></Tooltip>
                          ) : fmtInt(r.matchedWebIds)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>{r.updatedByEmail || ''}</Typography>
                          <NyTime ts={r.updatedAt} variant="caption" />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><MdEdit /></IconButton></Tooltip>
                          <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => setRemoveRow(r)}><MdDelete /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Collapse>

      <Dialog open={!!form} onClose={formBusy ? undefined : () => setForm(null)} maxWidth="xs" fullWidth>
        {form && (
          <>
            <DialogTitle>{form.existing ? `Edit ${form.prefix}` : 'Add a code'}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <TextField
                  size="small" label="Code (start of a WebID)" value={form.prefix} autoFocus={!form.existing} disabled={form.existing}
                  onChange={(e) => setForm((f) => ({ ...f, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 32) }))}
                  error={form.prefix.length > 0 && !prefixOk}
                  helperText={existingRow
                    ? `${cleanPrefix} is already on the list (${modeInfo(existingRow.mode).label}); saving replaces it.`
                    : 'Letters, digits and dashes, e.g. CBN4 or BRD3.'}
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
                <TextField select size="small" label="Gets" value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}>
                  {SCENE_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" label="Label (optional)" value={form.label} error={!labelOk}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 60) }))}
                  helperText="Up to 60 characters, e.g. the collection name."
                />
                <TextField
                  size="small" label="Note (optional)" value={form.note} multiline minRows={2} error={!noteOk}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value.slice(0, 300) }))}
                />
              </Stack>
              {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setForm(null)} disabled={formBusy}>Cancel</Button>
              <Button variant="contained" sx={primaryButtonSx} onClick={save} disabled={formBusy || !prefixOk || !labelOk || !noteOk}>
                {formBusy ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!removeRow} busy={removeBusy} onClose={() => setRemoveRow(null)} onConfirm={doRemove}
        title={removeRow ? `Remove ${removeRow.prefix}?` : ''} confirmLabel="Remove" confirmColor="error"
      >
        <Typography>
          Rugs whose WebID starts with {removeRow ? removeRow.prefix : ''} follow a shorter code on the list, or the usual
          rule when none matches. Plans already made keep their shots.
        </Typography>
      </ConfirmDialog>
    </Paper>
  );
};

export default SceneListPanel;
